import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Fade
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useAuth } from '../../contexts/AuthContext';
import { getUserFollowers, followUser, unfollowUser, getFollowStatus, UserInfo } from '../../services/FollowService';
import UserAvatar from '../common/UserAvatar';
import { formatDistanceToNow } from 'date-fns';
import HttpClient from '../../services/HttpClient';
import { OPINION_SERVICE_HOST, OPINIONS_API_PREFIX } from '../../services/RestConstants';
import MainLayout from '../layout/MainLayout';

const FollowersPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [followers, setFollowers] = useState<UserInfo[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [followStatuses, setFollowStatuses] = useState<Map<string, boolean>>(new Map());
  const [loadingFollow, setLoadingFollow] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [userInfo, setUserInfo] = useState<{ username: string; profilePicture?: string } | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  const isOwnProfile = currentUser?._id === userId;

  useEffect(() => {
    if (userId) {
      loadUserInfo();
      loadFollowers(1, true);
    }
  }, [userId]);

  useEffect(() => {
    if (followers.length > 0 && currentUser?._id) {
      loadFollowStatuses();
    }
  }, [followers, currentUser?._id]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore]);

  const loadUserInfo = async () => {
    if (!userId) return;
    try {
      const response = await HttpClient.GET({
        BASE_URL: OPINION_SERVICE_HOST,
        END_POINT: `${OPINIONS_API_PREFIX}/users/${userId}`,
      });
      if (response.data.success) {
        setUserInfo({
          username: response.data.data.user.username,
          profilePicture: response.data.data.user.profilePicture
        });
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const loadFollowers = async (pageNum: number, isInitial: boolean = false) => {
    if (!userId) return;
    
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await getUserFollowers(userId, pageNum, 30);
      
      if (isInitial) {
        setFollowers(response.data.followers);
      } else {
        setFollowers(prev => [...prev, ...response.data.followers]);
      }

      setTotalCount(response.data.pagination.totalCount);
      setHasMore(pageNum < response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error loading followers:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadFollowers(nextPage, false);
    }
  }, [page, loadingMore, hasMore]);

  const loadFollowStatuses = async () => {
    if (!currentUser?._id) return;

    const statusMap = new Map<string, boolean>();
    const promises = followers.map(async (follower) => {
      try {
        const status = await getFollowStatus(follower._id);
        statusMap.set(follower._id, status.isFollowing);
      } catch (error) {
        console.error(`Error loading follow status for ${follower._id}:`, error);
      }
    });

    await Promise.all(promises);
    setFollowStatuses(statusMap);
  };

  const handleFollow = async (targetUserId: string) => {
    if (!currentUser?._id) {
      navigate('/login');
      return;
    }

    try {
      setLoadingFollow(prev => new Set(prev).add(targetUserId));
      const isFollowing = followStatuses.get(targetUserId) || false;
      
      if (isFollowing) {
        await unfollowUser(targetUserId);
        setFollowStatuses(prev => {
          const next = new Map(prev);
          next.set(targetUserId, false);
          return next;
        });
      } else {
        await followUser(targetUserId);
        setFollowStatuses(prev => {
          const next = new Map(prev);
          next.set(targetUserId, true);
          return next;
        });
      }
    } catch (error: any) {
      console.error('Error following/unfollowing:', error);
      alert(error.response?.data?.error || 'Failed to update follow status');
    } finally {
      setLoadingFollow(prev => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });
    }
  };


  if (loading) {
    return (
      <MainLayout>
        <Helmet>
          <title>Followers | OpinLoop</title>
        </Helmet>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Followers">
      <Helmet>
        <title>{userInfo?.username ? `${userInfo.username}'s Followers` : 'Followers'} | OpinLoop</title>
        <meta name="description" content={`View all followers of ${userInfo?.username || 'this user'} on OpinLoop`} />
      </Helmet>
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: { xs: 2, md: 2.5 },
                pt: { xs: 2, md: 2.5 },
              }}
            >
              <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        color: 'text.primary'
                      }}
                    >
                      Followers
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {userInfo?.username ? `${userInfo.username}'s` : 'All'} Followers
                    </Typography>
                  </Box>
                  <Chip
                    icon={<PeopleIcon sx={{ fontSize: 18 }} />}
                    label={`${totalCount} ${totalCount === 1 ? 'Follower' : 'Followers'}`}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontSize: '0.9375rem',
                      height: 36,
                      fontWeight: 600,
                      px: 1
                    }}
                  />
                </Box>
              </Container>
            </Box>

            {/* Followers List */}
            <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 3 }, pb: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
              {followers.length === 0 ? (
                <Fade in timeout={800}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2, mt: 4 }}>
                    <CardContent sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
                      <PeopleIcon sx={{ fontSize: { xs: 64, sm: 80 }, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h5" fontWeight={600} sx={{ mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        No Followers Yet
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, maxWidth: 500, mx: 'auto' }}>
                        Start sharing great content to attract followers!
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    mt: 4
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {followers.map((follower, index) => {
                      const isFollowing = followStatuses.get(follower._id) || false;
                      const isLoading = loadingFollow.has(follower._id);
                      const isCurrentUser = follower._id === currentUser?._id;

                      return (
                        <Fade in timeout={600} key={follower._id} style={{ transitionDelay: `${index * 30}ms` }}>
                          <ListItem
                            sx={{
                              px: { xs: 2, sm: 3 },
                              py: 2.5,
                              borderBottom: index < followers.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'action.hover',
                                transform: 'translateX(4px)'
                              }
                            }}
                          >
                            <ListItemAvatar>
                              <Box
                                sx={{
                                  position: 'relative',
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: -2,
                                    left: -2,
                                    right: -2,
                                    bottom: -2,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    zIndex: -1,
                                    opacity: 0.3
                                  }
                                }}
                              >
                                <UserAvatar
                                  profileIcon={follower.profilePicture}
                                  username={follower.username}
                                  size={56}
                                  sx={{
                                    bgcolor: 'primary.main',
                                    border: '2px solid white',
                                    boxShadow: 2
                                  }}
                                />
                              </Box>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '1.125rem',
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                                    }}
                                    onClick={() => navigate(`/profile/${follower._id}`)}
                                  >
                                    {follower.username}
                                  </Typography>
                                  {index < 3 && (
                                    <VerifiedUserIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                  )}
                                  {isCurrentUser && (
                                    <Chip
                                      label="You"
                                      size="small"
                                      sx={{
                                        bgcolor: 'primary.light',
                                        color: 'primary.dark',
                                        height: 20,
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.875rem', mt: 0.5 }}
                                >
                                  Joined {formatDistanceToNow(new Date(follower.createdAt), { addSuffix: true })}
                                </Typography>
                              }
                            />
                            <Box sx={{ ml: 2 }}>
                              {!isCurrentUser && currentUser?._id && (
                                <Button
                                  variant={isFollowing ? 'outlined' : 'contained'}
                                  size="small"
                                  startIcon={
                                    isLoading ? (
                                      <CircularProgress size={14} />
                                    ) : isFollowing ? (
                                      <PersonRemoveIcon />
                                    ) : (
                                      <PersonAddIcon />
                                    )
                                  }
                                  onClick={() => handleFollow(follower._id)}
                                  disabled={isLoading}
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1,
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    minWidth: 120
                                  }}
                                >
                                  {isFollowing ? 'Unfollow' : 'Follow'}
                                </Button>
                              )}
                              {!currentUser?._id && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => navigate('/login')}
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1,
                                    fontWeight: 600
                                  }}
                                >
                                  View Profile
                                </Button>
                              )}
                            </Box>
                          </ListItem>
                        </Fade>
                      );
                    })}
                  </List>

                  {/* Infinite Scroll Trigger */}
                  {hasMore && (
                    <Box
                      ref={observerTarget}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 3,
                        minHeight: 60
                      }}
                    >
                      {loadingMore && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary">
                            Loading more followers...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {!hasMore && followers.length > 0 && (
                    <Box sx={{ textAlign: 'center', py: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        You've reached the end of the followers list
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Container>
    </MainLayout>
  );
};

export default FollowersPage;
